import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close,
  ContentCopy,
  Add,
  Delete,
  Visibility,
  VisibilityOff,
  VpnKey,
} from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import type { BackendUser } from "types/interfaces/adminUsers";
import { resolveSftpAuthMode } from "types/interfaces/sftp";
import {
  useGetSftpConnectionQuery,
  useGetSftpPublicConfigQuery,
  useLazyRevealSftpPasswordQuery,
  useUpdateSftpConnectionMutation,
  useDeleteSftpConnectionMutation,
  useLazyCheckSftpUsernameQuery,
} from "features/sftp/sftp";
import { useBackendUsersAutocompleteInfinite } from "features/adminUsers/useBackendUsersAutocompleteInfinite";
import {
  backendUserAutocompleteComponentsProps,
  BackendUserAutocompleteListbox,
  renderBackendUserAutocompleteOption,
} from "components/common/BackendUserLabelBlock";
import { formatBackendUserOptionLabel } from "utils/backendUserLabel";
import AppDialog from "components/common/AppDialog";
import CustomScrollbars from "components/CustomScrollbars";
import KeyTypePickerDialog from "./KeyTypePickerDialog";
import SftpFolderMountsEditor from "./SftpFolderMountsEditor";
import {
  comparableFolderMountsFromConnection,
  folderMountRowsFromConnection,
  folderMountsToPayload,
  newFolderMountRow,
  type FolderMountRow,
} from "./sftpFolderMountUtils";
import SftpFolderPickerDialog, {
  FolderPickerResult,
} from "./SftpFolderPickerDialog";

const STORED_PASSWORD_MASK = "\u2022".repeat(12);

type AuthMethod = "password" | "key" | "both";

function generateSecurePassword(length = 12): string {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const all = upper + lower + digits;
  const arr = new Uint32Array(length);
  let pw: string;
  do {
    crypto.getRandomValues(arr);
    pw = Array.from(arr, (v) => all[v % all.length]).join("");
  } while (!/[a-z]/.test(pw) || !/[A-Z]/.test(pw) || !/[0-9]/.test(pw));
  return pw;
}

interface SftpConnectionDetailProps {
  connectionId: number;
  readOnly?: boolean;
  onClose: () => void;
  onDeleted: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
        px: 1,
        borderRadius: 1,
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ minWidth: 110 }}
      >
        {label}
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{ flex: 1, justifyContent: "flex-end" }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: mono ? "monospace" : undefined,
            fontSize: mono ? 12 : undefined,
            wordBreak: "break-all",
            textAlign: "right",
          }}
        >
          {value || "—"}
        </Typography>
        {value && (
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
            <ContentCopy
              sx={{
                fontSize: 14,
                color: copied ? "success.main" : "text.disabled",
              }}
            />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default function SftpConnectionDetail({
  connectionId,
  readOnly = false,
  onClose,
  onDeleted,
  onDirtyChange,
  onSavingChange,
}: SftpConnectionDetailProps) {
  const isViewOnly = readOnly;

  const chipGroupFadeSx = isViewOnly ? { opacity: 0.58 } : {};

  const { data: connection, isLoading } =
    useGetSftpConnectionQuery(connectionId);
  const { data: publicConfig } = useGetSftpPublicConfigQuery();
  const sftpHost = publicConfig?.public_hostname ?? "sftp.calibratehcm.com";
  const usernameCheckServerId =
    connection?.server_id ??
    publicConfig?.default_server_id ??
    "s-84190f80606e405c9";

  const [updateConn, { isLoading: isSaving }] =
    useUpdateSftpConnectionMutation();
  const [deleteConn, { isLoading: isDeleting }] =
    useDeleteSftpConnectionMutation();
  const [triggerRevealPassword] = useLazyRevealSftpPasswordQuery();
  const [checkUsername] = useLazyCheckSftpUsernameQuery();

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [serverPasswordCache, setServerPasswordCache] = useState<string | null>(
    null,
  );
  const [bucket, setBucket] = useState("");
  const [folderMountRows, setFolderMountRows] = useState<FolderMountRow[]>([]);
  const [folderPickerRowIndex, setFolderPickerRowIndex] = useState<
    number | null
  >(null);
  const [role, setRole] = useState<"read" | "write">("read");
  const [isActive, setIsActive] = useState(true);
  const [authMethod, setAuthMethod] = useState<AuthMethod>("password");
  const [selectedUser, setSelectedUser] = useState<BackendUser | null>(null);
  const [userSearch, setUserSearch] = useState("");

  const {
    options: backendUserOptions,
    loading: backendUsersLoading,
    onListboxScroll: backendUsersListboxOnScroll,
  } = useBackendUsersAutocompleteInfinite({
    page_size: 50,
    search: userSearch,
    sort_by: "name",
    sort_order: "asc",
    value: selectedUser,
  });

  const [usernameError, setUsernameError] = useState("");
  const [nameError, setNameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const usernameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [addKeyOpen, setAddKeyOpen] = useState(false);
  const [newKeyText, setNewKeyText] = useState("");
  const [pendingKeyAdds, setPendingKeyAdds] = useState<string[]>([]);
  const [pendingKeyRemoveIds, setPendingKeyRemoveIds] = useState<number[]>([]);
  const [deleteKeyTarget, setDeleteKeyTarget] = useState<
    { type: "server"; id: number } | { type: "pending"; index: number } | null
  >(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordDirty, setPasswordDirty] = useState(false);
  const [isRevealingPw, setIsRevealingPw] = useState(false);
  const [keyPickerOpen, setKeyPickerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [closeDiscardOpen, setCloseDiscardOpen] = useState(false);
  const [hydratedSignature, setHydratedSignature] = useState<string | null>(
    null,
  );

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message: string, severity: "success" | "error") => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    setHydratedSignature(null);
  }, [connectionId]);

  useEffect(() => {
    setPendingKeyAdds([]);
    setPendingKeyRemoveIds([]);
  }, [connectionId]);

  useEffect(() => {
    if (!connection) return;
    setName(connection.name ?? "");
    setUsername(connection.username ?? "");
    setPassword("");
    setServerPasswordCache(null);
    setBucket(connection.bucket ?? "");
    setFolderMountRows(folderMountRowsFromConnection(connection));
    setRole((connection.role as "read" | "write") ?? "read");
    setIsActive(connection.is_active ?? true);
    setAuthMethod(resolveSftpAuthMode(connection));
    setUsernameError("");
    setNameError("");
    setPasswordError("");
    setPasswordVisible(false);
    setPasswordDirty(false);
    if (connection.id !== connectionId) return;
    setHydratedSignature(
      `${connection.id}:${String(connection.updated_at ?? "")}`,
    );
  }, [connection, connectionId, connection?.id, connection?.updated_at]);

  useEffect(() => {
    if (!connection?.user_id) {
      setSelectedUser(null);
      return;
    }
    const fromApi = connection.backend_user;
    if (fromApi && fromApi.id === connection.user_id) {
      setSelectedUser(fromApi);
      return;
    }
    const match = backendUserOptions.find((u) => u.id === connection.user_id);
    if (match) setSelectedUser(match);
  }, [
    connection?.user_id,
    connection?.updated_at,
    connection?.backend_user,
    backendUserOptions,
  ]);

  const showsPassword = authMethod === "password" || authMethod === "both";
  const showsKey = authMethod === "key" || authMethod === "both";

  useEffect(() => {
    if (authMethod === "key") {
      setPassword("");
      setServerPasswordCache(null);
      setPasswordVisible(false);
      setPasswordDirty(false);
      setPasswordError("");
    }
  }, [authMethod]);

  useEffect(() => {
    if (authMethod === "password") {
      setAddKeyOpen(false);
      setDeleteKeyTarget(null);
      setKeyPickerOpen(false);
      setNewKeyText("");
      setPendingKeyAdds([]);
      setPendingKeyRemoveIds([]);
    }
  }, [authMethod]);

  const validateUsername = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setUsernameError("");
        return;
      }
      if (trimmed.length < 3) {
        setUsernameError("Username must be at least 3 characters");
        return;
      }
      if (!/^[a-zA-Z0-9._-]+$/.test(trimmed)) {
        setUsernameError("Spaces are not permitted in usernames.");
        return;
      }
      if (usernameCheckTimer.current) clearTimeout(usernameCheckTimer.current);
      usernameCheckTimer.current = setTimeout(async () => {
        try {
          const result = await checkUsername({
            username: trimmed,
            exclude_id: connectionId,
            server_id: usernameCheckServerId,
          }).unwrap();
          setUsernameError(result.exists ? "Username already taken" : "");
        } catch {
          setUsernameError("");
        }
      }, 400);
    },
    [checkUsername, connectionId, usernameCheckServerId],
  );

  const validatePassword = useCallback((value: string) => {
    if (!value) {
      setPasswordError("");
      return;
    }
    if (value.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    if (!/[a-z]/.test(value)) {
      setPasswordError("Must contain a lowercase letter");
      return;
    }
    if (!/[A-Z]/.test(value)) {
      setPasswordError("Must contain an uppercase letter");
      return;
    }
    if (!/[0-9]/.test(value)) {
      setPasswordError("Must contain a number");
      return;
    }
    setPasswordError("");
  }, []);

  const showStoredPasswordMask =
    !!connection &&
    connection.has_password &&
    showsPassword &&
    !password &&
    !passwordVisible &&
    !passwordDirty;

  const passwordFieldValue = showStoredPasswordMask
    ? STORED_PASSWORD_MASK
    : password.trim()
      ? password
      : passwordVisible && serverPasswordCache !== null
        ? serverPasswordCache
        : "";

  const needsNewPasswordWhenNoneStored =
    !!connection &&
    showsPassword &&
    !connection.has_password &&
    !password.trim();

  const keyCountForValidation = !connection
    ? 0
    : connection.public_keys.filter((k) => !pendingKeyRemoveIds.includes(k.id))
        .length + pendingKeyAdds.length;
  const needsKeysWhenKeyOnly =
    !!connection && authMethod === "key" && keyCountForValidation === 0;

  const mountsPayload = folderMountsToPayload(folderMountRows);
  const currentMountsKey = JSON.stringify(mountsPayload);

  const isFormValid =
    !!name.trim() &&
    !nameError &&
    !!username.trim() &&
    !usernameError &&
    !!selectedUser &&
    !!bucket.trim() &&
    folderMountRows.length > 0 &&
    folderMountRows.every((m) => m.prefix.trim()) &&
    (folderMountRows.length <= 1 ||
      (folderMountRows.every((m) => m.label.trim()) &&
        new Set(folderMountRows.map((m) => m.label.trim())).size ===
          folderMountRows.length)) &&
    !needsNewPasswordWhenNoneStored &&
    !needsKeysWhenKeyOnly &&
    (!password || !passwordError);

  const connectionSignature = connection
    ? `${connection.id}:${String(connection.updated_at ?? "")}`
    : "";
  const isFormHydrated =
    !!connection &&
    connection.id === connectionId &&
    hydratedSignature === connectionSignature;

  const userResolvePending =
    !!connection &&
    connection.id === connectionId &&
    connection.user_id != null &&
    selectedUser?.id !== connection.user_id &&
    backendUsersLoading;

  const keyDraftDirty =
    pendingKeyAdds.length > 0 || pendingKeyRemoveIds.length > 0;

  const hasChanges =
    !isViewOnly &&
    isFormHydrated &&
    !userResolvePending &&
    !!connection &&
    (keyDraftDirty ||
      (connection.name ?? "") !== name.trim() ||
      connection.username !== username.trim() ||
      (connection.bucket ?? "") !== bucket.trim() ||
      connection.role !== role ||
      connection.is_active !== isActive ||
      (connection.user_id ?? null) !== (selectedUser?.id ?? null) ||
      currentMountsKey !== comparableFolderMountsFromConnection(connection) ||
      (!!password.trim() && showsPassword) ||
      resolveSftpAuthMode(connection) !== authMethod);

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => {
    if (isViewOnly) onDirtyChange?.(false);
  }, [isViewOnly, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(isSaving || isDeleting);
  }, [isSaving, isDeleting, onSavingChange]);

  const buildSaveConfirmSections = (): {
    label: string;
    updated: string[];
  }[] => {
    if (!connection) return [];
    const out: { label: string; updated: string[] }[] = [];
    const profile: string[] = [];
    if ((connection.name ?? "").trim() !== name.trim())
      profile.push("Display name");
    if (connection.username !== username.trim()) profile.push("Username");
    if ((connection.user_id ?? null) !== (selectedUser?.id ?? null)) {
      profile.push("Backend user");
    }
    if (profile.length > 0) out.push({ label: "PROFILE", updated: profile });

    const folderChanged =
      (connection.bucket ?? "") !== bucket.trim() ||
      currentMountsKey !== comparableFolderMountsFromConnection(connection);
    if (folderChanged) {
      out.push({
        label: "S3 FOLDER",
        updated: ["Bucket, folder, or subfolders"],
      });
    }

    const access: string[] = [];
    if (connection.role !== role) access.push("Access role");
    if (connection.is_active !== isActive) access.push("Status");
    if (resolveSftpAuthMode(connection) !== authMethod) {
      access.push("Authentication method");
    }
    if (access.length > 0) out.push({ label: "ACCESS", updated: access });

    const cred: string[] = [];
    if (showsPassword && password.trim()) cred.push("Password");
    if (keyDraftDirty) {
      if (pendingKeyAdds.length > 0) {
        cred.push(`${pendingKeyAdds.length} public key(s) to add`);
      }
      if (pendingKeyRemoveIds.length > 0) {
        cred.push(`${pendingKeyRemoveIds.length} public key(s) to remove`);
      }
    }
    if (cred.length > 0) out.push({ label: "CREDENTIALS", updated: cred });
    return out;
  };

  const performSave = async () => {
    if (isViewOnly || !connection || !isFormValid) return;
    setSaveConfirmOpen(false);
    try {
      const mounts = folderMountsToPayload(folderMountRows);
      const body: Record<string, unknown> = {
        id: connection.id,
        name: name.trim(),
        username: username.trim(),
        bucket: bucket.trim(),
        folder: mounts[0]?.prefix ?? "",
        subfolders: mounts[0]?.subfolders ?? [],
        folder_mounts: mounts,
        role,
        is_active: isActive,
        user_id: selectedUser?.id ?? null,
      };
      if (showsPassword && password.trim()) {
        body.password = password.trim();
      }
      body.auth_mode = authMethod;
      if (keyDraftDirty) {
        const existingKeys = connection.public_keys
          .filter((k) => !pendingKeyRemoveIds.includes(k.id))
          .map((k) => k.public_key);
        body.public_keys = [...existingKeys, ...pendingKeyAdds];
      }
      await updateConn(body as Parameters<typeof updateConn>[0]).unwrap();
      setPassword("");
      setServerPasswordCache(null);
      setPasswordVisible(false);
      setPasswordDirty(false);
      setPendingKeyAdds([]);
      setPendingKeyRemoveIds([]);
      showToast("Connection saved.", "success");
    } catch {
      showToast("Failed to save connection.", "error");
    }
  };

  const handleSaveClick = () => {
    if (isViewOnly || !connection || !isFormValid || !hasChanges) return;
    const sections = buildSaveConfirmSections();
    if (sections.length === 0) {
      void performSave();
      return;
    }
    setSaveConfirmOpen(true);
  };

  const handleHeaderClose = () => {
    if (isViewOnly) {
      onClose();
      return;
    }
    if (hasChanges) setCloseDiscardOpen(true);
    else onClose();
  };

  const handleDeleteConfirm = async () => {
    if (!connection) return;
    try {
      await deleteConn(connection.id).unwrap();
      setDeleteConfirmOpen(false);
      setDeleteConfirmInput("");
      onDeleted();
    } catch {
      showToast("Failed to delete connection.", "error");
      setDeleteConfirmOpen(false);
    }
  };

  const handleFolderPicked = (result: FolderPickerResult) => {
    if (folderPickerRowIndex === null) return;
    setBucket(result.bucket);
    setFolderMountRows((prev) => {
      const next = [...prev];
      const row = next[folderPickerRowIndex];
      if (!row) return prev;
      next[folderPickerRowIndex] = {
        ...row,
        prefix: result.folder,
        subfolders: result.subfolders,
      };
      return next;
    });
    setFolderPickerRowIndex(null);
  };

  const handleAddKey = () => {
    if (!newKeyText.trim()) return;
    setAuthMethod("key");
    setPendingKeyAdds((prev) => [...prev, newKeyText.trim()]);
    setNewKeyText("");
    setAddKeyOpen(false);
  };

  const handleConfirmRemoveKey = () => {
    if (!deleteKeyTarget) return;
    if (deleteKeyTarget.type === "server") {
      setPendingKeyRemoveIds((prev) => [...prev, deleteKeyTarget.id]);
    } else {
      setPendingKeyAdds((prev) =>
        prev.filter((_, i) => i !== deleteKeyTarget.index),
      );
    }
    setDeleteKeyTarget(null);
  };

  const handlePasswordEye = async () => {
    if (!connection) return;
    if (password.length > 0) {
      setPasswordVisible((v) => !v);
      return;
    }
    if (!connection.has_password) return;
    if (serverPasswordCache !== null) {
      setPasswordVisible((v) => !v);
      setPasswordError("");
      return;
    }
    setIsRevealingPw(true);
    try {
      const result = await triggerRevealPassword(connectionId).unwrap();
      setServerPasswordCache(result.password);
      setPasswordVisible(true);
      setPasswordError("");
    } catch {
      showToast("Failed to reveal password.", "error");
    } finally {
      setIsRevealingPw(false);
    }
  };

  const handleCopyConnectionInfo = () => {
    const lines = [
      `Host: ${sftpHost}`,
      `Username: ${username.trim() || connection?.username}`,
    ];
    const pw = password.trim() || (serverPasswordCache ?? "").trim();
    if (connection?.has_password && pw) lines.push(`Password: ${pw}`);
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      showToast("Connection info copied.", "success");
    });
  };

  const handleKeyGenerated = (publicKeyOpenSSH: string) => {
    if (isViewOnly) return;
    setAuthMethod("key");
    setPendingKeyAdds((prev) => [...prev, publicKeyOpenSSH]);
    setKeyPickerOpen(false);
    showToast(
      "Key pair generated. SSH key bundle (ZIP) downloaded. Save changes to apply the public key.",
      "success",
    );
  };

  const isBusy = isSaving || isDeleting;

  if (isLoading || !connection) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ p: 6 }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  const headerTitle = name.trim() || connection.username;

  const visibleServerKeys = connection.public_keys.filter(
    (k) => !pendingKeyRemoveIds.includes(k.id),
  );
  const displayKeyCount = visibleServerKeys.length + pendingKeyAdds.length;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
            {headerTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            @{username.trim() || connection.username}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {connection.is_active ? (
            <Chip
              label="Active"
              size="small"
              sx={{
                bgcolor: "rgba(46,125,50,0.10)",
                color: "success.dark",
                fontWeight: 600,
              }}
            />
          ) : (
            <Chip
              label="Inactive"
              size="small"
              sx={{
                bgcolor: "rgba(198, 40, 40, 0.10)",
                color: "#c62828",
                fontWeight: 600,
              }}
            />
          )}
          {(authMethod === "password" || authMethod === "both") && (
            <Chip
              label="Password"
              size="small"
              sx={{
                fontSize: 11,
                height: 22,
                bgcolor: "rgba(25,118,210,0.08)",
                color: "info.dark",
                fontWeight: 600,
              }}
            />
          )}
          {(authMethod === "key" || authMethod === "both") && (
            <Chip
              label="Key"
              size="small"
              sx={{
                fontSize: 11,
                height: 22,
                bgcolor: "#f3e5f5",
                color: "#6a1b9a",
                fontWeight: 600,
              }}
            />
          )}
          <IconButton size="small" onClick={handleHeaderClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <Box
            sx={{
              mb: 3,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "calibrate.primarySoft04",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing={0.6}
              >
                Quick Copy
              </Typography>
              <Tooltip title="Copy host, username & password">
                <IconButton size="small" onClick={handleCopyConnectionInfo}>
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.8 }}
            >
              Host: {sftpHost}
              <br />
              Username: {username.trim() || connection.username}
              {connection.has_password && (
                <>
                  <br />
                  Password:{" "}
                  {passwordVisible && (password || serverPasswordCache)
                    ? password || serverPasswordCache
                    : "••••••••"}
                </>
              )}
            </Typography>
            {connection.has_password && (
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handlePasswordEye}
                  disabled={isRevealingPw}
                >
                  {isRevealingPw ? (
                    <CircularProgress size={14} />
                  ) : passwordVisible ? (
                    <Visibility sx={{ fontSize: 16 }} />
                  ) : (
                    <VisibilityOff sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Box>
            )}
          </Box>

          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing={0.6}
            display="block"
            sx={{ mb: 1 }}
          >
            Connection and folder
          </Typography>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}
          >
            <Autocomplete
              options={backendUserOptions}
              loading={backendUsersLoading}
              componentsProps={backendUserAutocompleteComponentsProps}
              ListboxComponent={BackendUserAutocompleteListbox}
              ListboxProps={{ onScroll: backendUsersListboxOnScroll }}
              disabled={isViewOnly}
              value={selectedUser}
              onChange={(_e, v) => {
                if (isViewOnly) return;
                setSelectedUser(v);
                setBucket("");
                setFolderMountRows([newFolderMountRow()]);
              }}
              getOptionLabel={formatBackendUserOptionLabel}
              renderOption={renderBackendUserAutocompleteOption}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Backend User *"
                  onChange={(e) => setUserSearch(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              )}
            />

            <TextField
              label="Display Name *"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (e.target.value.trim()) setNameError("");
              }}
              onBlur={() => {
                if (!name.trim()) setNameError("");
              }}
              size="small"
              fullWidth
              disabled={isViewOnly}
              error={!!nameError}
              helperText={nameError || "Internal label"}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />

            <TextField
              label="Username *"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (e.target.value.trim()) validateUsername(e.target.value);
                else setUsernameError("");
              }}
              onBlur={() => {
                if (username.trim()) validateUsername(username);
                else setUsernameError("");
              }}
              size="small"
              fullWidth
              disabled={isViewOnly}
              error={!!usernameError}
              helperText={usernameError || undefined}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />

            <Box
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <InfoRow label="SFTP Host" value={sftpHost} mono />
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ mb: 0.5, display: "block" }}
              >
                Authentication Method
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" sx={chipGroupFadeSx}>
                {(["password", "key", "both"] as const).map((m) => (
                  <Chip
                    key={m}
                    label={
                      m === "both"
                        ? "Both"
                        : m === "key"
                          ? "Public Key"
                          : "Password"
                    }
                    size="small"
                    clickable={!isViewOnly}
                    onClick={() => {
                      if (isViewOnly) return;
                      setAuthMethod(m);
                    }}
                    sx={
                      authMethod === m
                        ? {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontWeight: 700,
                          }
                        : { fontWeight: 600 }
                    }
                  />
                ))}
              </Box>
            </Box>

            {showsKey && (
              <>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing={0.6}
                  >
                    Public Keys ({displayKeyCount})
                  </Typography>
                  {!isViewOnly && (
                    <Box
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VpnKey />}
                        onClick={() => {
                          setAuthMethod("key");
                          setKeyPickerOpen(true);
                        }}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Generate Key Pair
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => {
                          setAuthMethod("key");
                          setAddKeyOpen(true);
                        }}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Add Key
                      </Button>
                    </Box>
                  )}
                </Box>

                {displayKeyCount === 0 ? (
                  <Box
                    sx={{
                      py: 2,
                      textAlign: "center",
                      borderRadius: 1,
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No public keys configured
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {visibleServerKeys.map((key) => (
                      <Box
                        key={key.id}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          p: 1.5,
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 11,
                              wordBreak: "break-all",
                              color: "text.secondary",
                              lineHeight: 1.4,
                            }}
                          >
                            {key.public_key.length > 120
                              ? `${key.public_key.slice(0, 60)}...${key.public_key.slice(-30)}`
                              : key.public_key}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            Added{" "}
                            {new Date(key.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(key.public_key);
                              showToast("Public key copied.", "success");
                            }}
                            sx={{ flexShrink: 0 }}
                          >
                            <ContentCopy
                              fontSize="small"
                              sx={{ color: "text.disabled" }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setDeleteKeyTarget({ type: "server", id: key.id })
                            }
                            disabled={isViewOnly}
                            sx={{ flexShrink: 0 }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                    {pendingKeyAdds.map((pk, idx) => (
                      <Box
                        key={`pending-${idx}-${pk.slice(0, 20)}`}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "warning.main",
                          p: 1.5,
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 11,
                              wordBreak: "break-all",
                              color: "text.secondary",
                              lineHeight: 1.4,
                            }}
                          >
                            {pk.length > 120
                              ? `${pk.slice(0, 60)}...${pk.slice(-30)}`
                              : pk}
                          </Typography>
                          <Typography variant="caption" color="warning.main">
                            Pending save
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(pk);
                              showToast("Public key copied.", "success");
                            }}
                            sx={{ flexShrink: 0 }}
                          >
                            <ContentCopy
                              fontSize="small"
                              sx={{ color: "text.disabled" }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setDeleteKeyTarget({
                                type: "pending",
                                index: idx,
                              })
                            }
                            disabled={isViewOnly}
                            sx={{ flexShrink: 0 }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}

            {showsPassword && (
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  label="Password"
                  value={passwordFieldValue}
                  onChange={(e) => {
                    if (isViewOnly) return;
                    const v = e.target.value;
                    if (!v && connection.has_password) {
                      setPassword("");
                      setPasswordDirty(false);
                      setPasswordVisible(false);
                      setPasswordError("");
                      return;
                    }
                    setPassword(v);
                    validatePassword(v);
                  }}
                  onFocus={() => {
                    if (showStoredPasswordMask) setPasswordDirty(true);
                  }}
                  onBlur={() => {
                    if (
                      connection.has_password &&
                      !password.trim() &&
                      passwordDirty
                    ) {
                      setPasswordDirty(false);
                      setPasswordVisible(false);
                    }
                  }}
                  size="small"
                  fullWidth
                  error={!!passwordError}
                  helperText={
                    passwordError ||
                    (connection.has_password
                      ? ""
                      : "Set a password for this connection.")
                  }
                  type={passwordVisible ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder={
                    connection.has_password && !showStoredPasswordMask
                      ? "Reveal saved password or type new"
                      : undefined
                  }
                  InputProps={{
                    readOnly: showStoredPasswordMask || isViewOnly,
                    sx: {
                      fontFamily: "monospace",
                      fontSize: 13,
                      borderRadius: 1,
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handlePasswordEye}
                          disabled={
                            isRevealingPw ||
                            (!connection.has_password &&
                              password.length === 0 &&
                              !showStoredPasswordMask)
                          }
                          edge="end"
                          aria-label={
                            password.length > 0
                              ? "Toggle password visibility"
                              : "Load saved password"
                          }
                        >
                          {isRevealingPw ? (
                            <CircularProgress size={14} />
                          ) : passwordVisible ? (
                            <Visibility fontSize="small" />
                          ) : (
                            <VisibilityOff fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      ...(isViewOnly ? { bgcolor: "action.hover" } : {}),
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const pw = generateSecurePassword();
                    setPassword(pw);
                    setPasswordVisible(true);
                    setPasswordError("");
                  }}
                  disabled={isViewOnly}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    mt: 0.25,
                    minWidth: 90,
                    borderRadius: 1,
                  }}
                >
                  Generate
                </Button>
              </Box>
            )}

            <SftpFolderMountsEditor
              readOnly={isViewOnly}
              bucket={bucket}
              setBucket={setBucket}
              selectedUser={selectedUser}
              folderMountRows={folderMountRows}
              setFolderMountRows={setFolderMountRows}
              setFolderPickerRowIndex={setFolderPickerRowIndex}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ mb: 0.5, display: "block" }}
              >
                Access Role
              </Typography>
              <Box display="flex" gap={1} sx={chipGroupFadeSx}>
                {(["read", "write"] as const).map((r) => (
                  <Chip
                    key={r}
                    label={r.charAt(0).toUpperCase() + r.slice(1)}
                    size="small"
                    clickable={!isViewOnly}
                    onClick={() => {
                      if (isViewOnly) return;
                      setRole(r);
                    }}
                    sx={
                      role === r
                        ? {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontWeight: 700,
                          }
                        : { fontWeight: 600 }
                    }
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ mb: 0.5, display: "block" }}
              >
                Status
              </Typography>
              <Box display="flex" gap={1} sx={chipGroupFadeSx}>
                {([true, false] as const).map((v) => (
                  <Chip
                    key={String(v)}
                    label={v ? "Active" : "Inactive"}
                    size="small"
                    clickable={!isViewOnly}
                    onClick={() => {
                      if (isViewOnly) return;
                      setIsActive(v);
                    }}
                    sx={
                      isActive === v
                        ? v
                          ? {
                              bgcolor: "rgba(46,125,50,0.10)",
                              color: "success.dark",
                              fontWeight: 700,
                              border: "1.5px solid",
                              borderColor: "success.dark",
                            }
                          : {
                              bgcolor: "rgba(198, 40, 40, 0.10)",
                              color: "#c62828",
                              fontWeight: 700,
                              border: "1.5px solid",
                              borderColor: "#c62828",
                            }
                        : { fontWeight: 600 }
                    }
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              sx={{ mb: 1 }}
            >
              Metadata
            </Typography>
            <Box
              sx={{
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <InfoRow
                label="Created"
                value={new Date(connection.created_at).toLocaleString(
                  undefined,
                  {
                    dateStyle: "medium",
                    timeStyle: "short",
                  },
                )}
              />
              <Divider />
              <InfoRow
                label="Updated"
                value={new Date(connection.updated_at).toLocaleString(
                  undefined,
                  {
                    dateStyle: "medium",
                    timeStyle: "short",
                  },
                )}
              />
            </Box>
          </Box>
        </Box>
      </CustomScrollbars>

      <Box
        px={2}
        pb={2}
        pt={1}
        sx={{
          flexShrink: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {hasChanges && !isViewOnly && (
          <Typography
            variant="caption"
            fontWeight={600}
            display="block"
            sx={{ mb: 1, color: "#ad1457" }}
          >
            Unsaved changes
          </Typography>
        )}
        <Tooltip
          title={
            isViewOnly
              ? "Super User required to save changes."
              : !hasChanges
                ? "No changes to save"
                : ""
          }
        >
          <span>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveClick}
              disabled={isViewOnly || !hasChanges || !isFormValid || isBusy}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 1,
                mb: 1,
              }}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={isViewOnly ? "Super User required to delete connections." : ""}
        >
          <span>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={isViewOnly || isBusy}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1 }}
            >
              Delete connection
            </Button>
          </span>
        </Tooltip>
      </Box>

      <AppDialog
        open={addKeyOpen}
        onClose={() => {
          setAddKeyOpen(false);
          setNewKeyText("");
        }}
        title="Add Public Key"
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => {
                setAddKeyOpen(false);
                setNewKeyText("");
              }}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddKey}
              disabled={!newKeyText.trim()}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Add Key
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste an OpenSSH public key below.
        </Typography>
        <TextField
          label="Public Key"
          multiline
          rows={5}
          value={newKeyText}
          onChange={(e) => setNewKeyText(e.target.value)}
          fullWidth
          placeholder="ssh-rsa AAAA..."
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: 12,
            },
          }}
        />
      </AppDialog>

      <AppDialog
        open={deleteKeyTarget !== null}
        onClose={() => setDeleteKeyTarget(null)}
        title="Remove Public Key"
        actions={
          <>
            <Button
              onClick={() => setDeleteKeyTarget(null)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmRemoveKey}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Remove
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to remove this public key? This action cannot be
          undone.
        </Typography>
      </AppDialog>

      <KeyTypePickerDialog
        open={keyPickerOpen}
        onClose={() => setKeyPickerOpen(false)}
        onGenerated={(pk) => handleKeyGenerated(pk)}
        username={username.trim() || connection.username}
        onError={(msg) => showToast(msg, "error")}
      />

      <SftpFolderPickerDialog
        open={folderPickerRowIndex !== null}
        onClose={() => setFolderPickerRowIndex(null)}
        onConfirm={handleFolderPicked}
        userId={selectedUser?.id}
        initialBucket={bucket}
        initialFolder={
          folderPickerRowIndex !== null
            ? (folderMountRows[folderPickerRowIndex]?.prefix ?? "")
            : ""
        }
        initialSubfolders={
          folderPickerRowIndex !== null
            ? (folderMountRows[folderPickerRowIndex]?.subfolders ?? [])
            : []
        }
      />

      <AppDialog
        open={deleteConfirmOpen}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        title="Delete SFTP Connection"
        actions={
          <>
            <Button
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteConfirmInput("");
              }}
              disabled={isDeleting}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={
                isDeleting ||
                (connection.username ?? "").trim() !== deleteConfirmInput.trim()
              }
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        <Typography
          variant="subtitle2"
          fontWeight={800}
          sx={{ color: "error.main", textTransform: "uppercase", mb: 1.5 }}
        >
          Warning
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Delete the SFTP connection for{" "}
          <strong>{connection.name || connection.username}</strong>? This will
          remove all associated public keys and credentials.
        </Typography>
        <TextField
          label="Type username to confirm"
          placeholder={connection.username ?? ""}
          value={deleteConfirmInput}
          onChange={(e) => setDeleteConfirmInput(e.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
        />
      </AppDialog>

      <AppDialog
        open={saveConfirmOpen}
        onClose={() => !isSaving && setSaveConfirmOpen(false)}
        title="Confirm Access Changes"
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => setSaveConfirmOpen(false)}
              disabled={isSaving}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void performSave()}
              disabled={isSaving}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {buildSaveConfirmSections().map((section, idx) => (
          <Box key={section.label}>
            {idx > 0 && <Divider sx={{ my: 2 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              sx={{ mb: 1 }}
            >
              {section.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              letterSpacing={0.5}
              sx={{ color: "info.main", display: "block", mb: 0.5 }}
            >
              Updated
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
              {section.updated.map((item) => (
                <Typography key={item} variant="body2" component="li">
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </AppDialog>

      <AppDialog
        open={closeDiscardOpen}
        onClose={() => setCloseDiscardOpen(false)}
        title="Unsaved changes"
        actions={
          <>
            <Button
              onClick={() => setCloseDiscardOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Stay
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setCloseDiscardOpen(false);
                onClose();
              }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Discard and continue
            </Button>
          </>
        }
      >
        <Typography
          variant="subtitle2"
          fontWeight={800}
          sx={{ color: "error.main", textTransform: "uppercase", mb: 1.5 }}
        >
          Warning
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You have unsaved changes to this SFTP connection. Stay to keep
          editing, or discard them and close the panel.
        </Typography>
      </AppDialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
